<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AvatarMediaSelector from '../common/AvatarMediaSelector.vue';
import MoneyInput from '../common/MoneyInput.vue';
import { useItemStatus } from '@/composables/commerce/useItemStatus.js';
import { useCurrency } from '@/composables/useCurrency.js';

// =============================================================================
// PROPS
// =============================================================================
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  avatarConfig: {
    type: Object,
    required: true,
  },
  availableMedia: {
    type: Array,
    default: () => [],
  },
  csrfToken: {
    type: String,
    default: '',
  },
  enableReservation: {
    type: Boolean,
    default: true,
  },
  boxShipName: {
    type: String,
    default: '',
  },
  fullDeposit: {
    type: Number,
    required: true,
  },
  isPlanLocked: {
    type: Boolean,
    default: false,
  },
  // The stepper's ceiling — 999 everywhere except a «تدبيسة» size, where the
  // editor passes this line's own cap (utils/tadbeesa.js resaleLineCap).
  maxQty: {
    type: Number,
    default: 999,
  },
  // A live «تدبيسة» line: born COLLECTED, yet the backend still takes QUANTITY
  // edits on it while its carton is open (OrderBoxService::resaleLineTakesEdits).
  // Opens the stepper only — note, photo, reservation and the cancel button keep
  // their status gate (the backend refuses a member cancel on a COLLECTED line;
  // she lowers the quantity instead).
  resaleEditable: {
    type: Boolean,
    default: false,
  },
  // This size has a limited count of pieces («تدبيسة»): at the cap the stepper
  // says so in words, not by a greyed + alone.
  limitedStock: {
    type: Boolean,
    default: false,
  },
});

// =============================================================================
// EMITS - Just forward everything to parent
// =============================================================================
defineEmits([
  'update:qty',
  'update:notes',
  'update:avatarUrl',
  'update:depositAmount',
  'update:reservedForName',
  'reservation-change',
  'remove',
  'restore',
  'avatar-upload-success',
  'avatar-upload-error',
]);

const { t } = useI18n();
const { formatCurrency } = useCurrency();

// =============================================================================
// STATUS HELPER
// =============================================================================
const statusHelper = useItemStatus(() => props.item);

// =============================================================================
// COMPUTED
// =============================================================================
const isEditable = computed(
  () => statusHelper.canEditQuantity.value && !props.isPlanLocked,
);
// The stepper alone may open wider than the other fields — see `resaleEditable`.
const isQtyEditable = computed(
  () => (statusHelper.canEditQuantity.value || props.resaleEditable) && !props.isPlanLocked,
);
const isCancelled = computed(() => statusHelper.isCancelled.value);
// A line whose quantity she can still change is not "read only", even when its
// status alone would say so (a live تدبيسة line).
const isReadonly = computed(
  () => (statusHelper.isReadOnly.value || props.isPlanLocked) && !isQtyEditable.value,
);
// F7's stepper only CLAMPS at max — it never greys its + — so the cap is shown
// here: the + is switched off (see .stepper-at-max) and, on a limited size, a
// sentence says why.
const isAtMax = computed(() => isQtyEditable.value && (props.item.qty || 0) >= props.maxQty);

// Reservation is rarely used — keep it collapsed behind a toggle so it can't be
// set by accident. Open by default only when the item already carries one.
const isReserved = computed(
  () => props.item.reservationStatus && props.item.reservationStatus !== 'none',
);
const reservedAmount = computed(() =>
  props.item.reservationStatus === 'full'
    ? props.fullDeposit
    : props.item.depositAmount || 0,
);
const reserveOpen = ref(isReserved.value);

// Same opt-in treatment for the note.
const hasNote = computed(() => Boolean((props.item.notes || '').trim()));
const notePreview = computed(() => {
  const note = (props.item.notes || '').trim();
  return note.length > 20 ? `${note.slice(0, 20)}…` : note;
});
const noteOpen = ref(hasNote.value);
</script>

<template>
  <div
    :data-item-index="index"
    class="item-card"
    :class="{
      'item-cancelled': isCancelled,
      'item-readonly': isReadonly && !isCancelled
    }"
  >
    <!-- Status Badge -->
    <div v-if="isCancelled" class="status-badge cancelled">
      <f7-icon f7="xmark_circle_fill" size="14"></f7-icon>
      <span>{{ t('item.fields.cancelled') }}</span>
    </div>
    <div v-else-if="isReadonly" class="status-badge readonly">
      <f7-icon f7="lock_fill" size="14"></f7-icon>
      <span>{{ t('item.fields.readonly') }}</span>
    </div>

    <!-- Left: Avatar + Action Button -->
    <div class="item-left">
      <avatar-media-selector
        :model-value="item.avatarUrl"
        :available-media="availableMedia"
        :server-url="avatarConfig.serverUrl"
        :accepted-file-types="avatarConfig.acceptTypes"
        :max-file-size="avatarConfig.maxSize"
        :placeholder-text="t('item.fields.uploadAvatar')"
        :csrf-token="csrfToken"
        :disabled="!isEditable"
        @update:model-value="$emit('update:avatarUrl', $event)"
        @upload-success="$emit('avatar-upload-success', $event)"
        @upload-error="$emit('avatar-upload-error', $event)"
      ></avatar-media-selector>

      <!-- Delete/Cancel/Restore Button -->
      <f7-button
        v-if="isCancelled"
        small
        fill
        color="green"
        class="action-btn"
        @click="$emit('restore')"
      >
        <f7-icon f7="arrow_clockwise_circle_fill" size="14"></f7-icon>
        <span class="action-label">{{ t('item.status.actions.restore') }}</span>
      </f7-button>
      <f7-button
        v-else
        small
        fill
        :color="item.existingId ? 'orange' : 'red'"
        class="action-btn"
        :disabled="!isEditable"
        @click="$emit('remove')"
      >
        <f7-icon :f7="item.existingId ? 'xmark_circle_fill' : 'trash_fill'" size="14"></f7-icon>
        <span class="action-label">{{ item.existingId ? t('item.status.actions.cancel') : t('item.status.actions.delete') }}</span>
      </f7-button>
    </div>

    <!-- Right: Controls -->
    <div class="item-right">
      <!-- Stepper -->
      <div class="item-stepper">
        <span class="qty-label">{{ t('variant.summary.quantity') }}</span>
        <!-- Keyed on the cap: framework7-vue hands `max` to F7 once, at create,
             and never watches it — a cap that drops after a refusal would
             otherwise never reach the stepper. -->
        <f7-stepper
          :key="maxQty"
          :value="item.qty"
          :min="0"
          :max="maxQty"
          small
          fill
          raised
          :class="{ 'stepper-at-max': isAtMax }"
          :disabled="!isQtyEditable"
          @stepper:change="$emit('update:qty', $event)"
        ></f7-stepper>
      </div>

      <!-- At the size's cap — in words, so the greyed + isn't the only signal. -->
      <div
        v-if="limitedStock && isAtMax && item.qty > 0"
        class="validation-message max-reached"
        role="status"
      >
        <f7-icon f7="info_circle_fill" size="12"></f7-icon>
        <span>{{ t('variant.resale.maxReached') }}</span>
      </div>

      <!-- Qty Error -->
      <div
        v-if="item.qty === 0 && isQtyEditable"
        class="validation-message error"
      >
        <f7-icon f7="exclamationmark_circle_fill" size="12"></f7-icon>
        <span>{{ t('item.fields.cannotSaveZeroQty') }}</span>
      </div>

      <!-- Reservation Section (opt-in — collapsed unless already reserved) -->
      <div v-if="enableReservation" class="item-reservation">
        <button
          type="button"
          class="disclosure-toggle"
          :class="{ 'disclosure-active': isReserved }"
          :aria-expanded="reserveOpen ? 'true' : 'false'"
          @click="reserveOpen = !reserveOpen"
        >
          <f7-icon f7="bookmark" size="18"></f7-icon>
          <span class="disclosure-label">
            {{ isReserved ? t('item.reservation.toggleSet', { amount: formatCurrency(reservedAmount) }) : t('item.reservation.toggleEmpty') }}
          </span>
          <f7-icon :f7="reserveOpen ? 'chevron_up' : 'chevron_down'" size="14"></f7-icon>
        </button>

        <div v-if="reserveOpen" class="reservation-body">
          <f7-segmented strong tag="div" class="reservation-segmented">
            <f7-button
              small
              :active="item.reservationStatus === 'none'"
              :disabled="!isEditable"
              @click="$emit('reservation-change', 'none')"
            >
              {{ t('item.reservation.none') }}
            </f7-button>
            <f7-button
              small
              :active="item.reservationStatus === 'partial'"
              :disabled="!isEditable"
              @click="$emit('reservation-change', 'partial')"
            >
              {{ t('item.reservation.partial') }}
            </f7-button>
            <f7-button
              small
              :active="item.reservationStatus === 'full'"
              :disabled="!isEditable"
              @click="$emit('reservation-change', 'full')"
            >
              {{ t('item.reservation.full') }}
            </f7-button>
          </f7-segmented>

          <!-- Reservation Details -->
          <div v-if="item.reservationStatus !== 'none'" class="reservation-details">
            <!-- Reserved Name -->
            <f7-list strong inset no-hairlines class="reservation-name-list">
              <f7-list-input
                type="text"
                :value="item.reservedForName"
                :placeholder="boxShipName || t('item.reservation.reservedForPlaceholder')"
                :label="t('item.reservation.reservedForLabel')"
                floating-label
                clear-button
                :disabled="!isEditable"
                :required="item.reservationStatus !== 'none'"
                :minlength="5"
                validate
                :error-message="t('item.reservation.nameRequired')"
                @input="$emit('update:reservedForName', $event.target.value)"
              >
                <template #media>
                  <f7-icon f7="person_circle_fill" size="20" color="green"></f7-icon>
                </template>
              </f7-list-input>
            </f7-list>

            <!-- Deposit Display -->
            <div class="deposit-display">
              <div v-if="item.reservationStatus === 'partial'" class="deposit-partial">
                <div class="field-label">{{ t('item.reservation.depositFieldLabel') }}</div>
                <money-input
                  :model-value="item.depositAmount"
                  :placeholder="t('item.reservation.depositAmount')"
                  :min="0"
                  :max="fullDeposit"
                  :disabled="!isEditable"
                  @update:model-value="$emit('update:depositAmount', $event)"
                ></money-input>
              </div>

              <div v-else-if="item.reservationStatus === 'full'" class="deposit-full">
                <f7-icon f7="checkmark_seal_fill" size="18" color="green"></f7-icon>
                <span class="deposit-label">{{ t('item.reservation.fullDeposit') }}</span>
                <span class="deposit-value">{{ formatCurrency(item.depositAmount) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Comment Section (opt-in) -->
      <div class="item-comment">
        <button
          type="button"
          class="disclosure-toggle"
          :class="{ 'disclosure-active': item.notes }"
          :aria-expanded="noteOpen ? 'true' : 'false'"
          @click="noteOpen = !noteOpen"
        >
          <f7-icon :f7="item.notes ? 'doc_text_fill' : 'doc_text'" size="18"></f7-icon>
          <span class="disclosure-label">
            {{ item.notes ? t('item.fields.notePreview', { preview: notePreview }) : t('item.fields.addNote') }}
          </span>
          <f7-icon :f7="noteOpen ? 'chevron_up' : 'chevron_down'" size="14"></f7-icon>
        </button>

        <f7-list
          v-if="noteOpen"
          strong
          inset
          no-hairlines
          class="comment-list"
        >
          <f7-list-input
            type="textarea"
            :value="item.notes"
            :placeholder="t('item.fields.notePlaceholder')"
            :disabled="!isEditable"
            @input="$emit('update:notes', $event.target.value)"
          >
            <template #media>
              <f7-icon
                :f7="item.notes ? 'doc_text_fill' : 'doc_text'"
                size="20"
                :color="item.notes ? 'primary' : 'gray'"
              ></f7-icon>
            </template>
          </f7-list-input>
        </f7-list>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Item Card */
.item-card {
    position: relative;
    display: flex;
    gap: 12px;
    padding: 12px;
    background: var(--f7-card-bg-color);
    border: 1px solid var(--f7-card-outline-border-color);
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    transition: all 0.2s;
}

.item-card:active {
    transform: scale(0.99);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.item-card.item-cancelled {
    opacity: 0.85;
    background: repeating-linear-gradient(
        45deg,
        var(--f7-card-bg-color),
        var(--f7-card-bg-color) 10px,
        rgba(239, 68, 68, 0.05) 10px,
        rgba(239, 68, 68, 0.05) 20px
    );
    border-color: #fca5a5;
}

.item-card.item-readonly {
    opacity: 0.8;
    background: linear-gradient(
        135deg,
        rgba(156, 163, 175, 0.05),
        rgba(156, 163, 175, 0.02)
    );
}

/* Status Badge */
.status-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    z-index: 5;
}

.status-badge.cancelled {
    background: rgba(239, 68, 68, 0.15);
    color: #dc2626;
    border: 1px solid rgba(239, 68, 68, 0.3);
}

.status-badge.readonly {
    background: rgba(156, 163, 175, 0.15);
    color: #6b7280;
    border: 1px solid rgba(156, 163, 175, 0.3);
}

.item-left {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
}

.action-btn {
    width: 90px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 12px;
    border-radius: 8px;
}

.action-label {
    font-size: 12px;
    font-weight: 600;
}

.item-right {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
}

/* Stepper */
.item-stepper {
    width: 100%;
}

.item-stepper :deep(.stepper) {
    width: 100%;
}

/* Validation Message */
.validation-message {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    padding: 4px 8px;
    border-radius: 6px;
}

.validation-message.error {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
}

/* A fact, not an error — the muted pair the editor's inline strips use, which
   carries its own dark-mode value. */
.validation-message.max-reached {
    color: var(--ci-muted-fg);
    background: var(--ci-muted-bg);
    font-size: 12px;
    line-height: 1.4;
}

/* At the cap F7 just clamps, so switch the + off by hand. */
.stepper-at-max :deep(.stepper-button-plus) {
    opacity: 0.35;
    pointer-events: none;
}

/* Reservation Section */
.item-reservation {
    width: 100%;
}

.reservation-body {
    direction: ltr;
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.item-comment {
    width: 100%;
}

/* Opt-in disclosure toggle (shared by reservation + comment) */
.disclosure-toggle {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    padding: 8px 12px;
    background: none;
    border: 1px solid var(--f7-list-item-border-color, rgba(0, 0, 0, 0.12));
    border-radius: 10px;
    color: var(--f7-theme-color);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}

.disclosure-toggle .disclosure-label {
    flex: 1;
    text-align: start;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.disclosure-toggle.disclosure-active {
    background: rgba(var(--f7-theme-color-rgb), 0.08);
    border-color: rgba(var(--f7-theme-color-rgb), 0.3);
}

.disclosure-toggle:active {
    opacity: 0.7;
}

.qty-label,
.field-label {
    display: block;
    font-size: 11px;
    color: var(--f7-text-color);
    opacity: 0.7;
    margin-bottom: 4px;
}

.reservation-segmented {
    width: 100%;
    margin-bottom: 8px;
}

.reservation-segmented :deep(.segmented) {
    width: 100%;
    background: #f1f5f9;
    border-radius: 8px;
    padding: 2px;
}

.reservation-segmented :deep(.button) {
    font-size: 12px;
    min-width: 0;
    flex: 1;
    padding: 6px 4px;
    font-weight: 500;
}

.reservation-segmented :deep(.button.button-active) {
    background: var(--f7-theme-color);
    color: white;
    font-weight: 600;
}

/* Reservation Details */
.reservation-details {
    animation: slideDown 0.2s ease;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-5px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Reserved Name Input */
.reservation-name-list {
    direction: rtl;
    margin: 0 !important;
    background: linear-gradient(
        135deg,
        rgba(16, 185, 129, 0.1),
        rgba(5, 150, 105, 0.05)
    );
    border: 1.5px solid rgba(16, 185, 129, 0.3);
    border-radius: 10px;
}

.reservation-name-list :deep(.list) {
    margin: 0;
}

.reservation-name-list :deep(.item-input-wrap) {
    margin-top: 0;
}

/* Deposit Display */
.deposit-display {
    animation: slideDown 0.2s ease;
}

.deposit-partial {
    width: 100%;
}

.deposit-full {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 8px;
}

.deposit-label {
    font-size: 13px;
    color: var(--f7-text-color);
}

.deposit-value {
    margin-right: auto;
    font-size: 14px;
    font-weight: 700;
    color: #10b981;
}

/* Comment Section */
.comment-list {
    direction: rtl;
    margin: 0 !important;
    background: var(--f7-page-bg-color);
    border: 1px solid var(--f7-list-item-border-color);
    border-radius: 8px;
}

.comment-list :deep(.list) {
    margin: 0;
}

.comment-list :deep(.item-input-wrap) {
    margin-top: 0;
}

.comment-list :deep(textarea) {
    min-height: 60px;
    resize: vertical;
}

/* Responsive */
@media (max-width: 380px) {
    .action-btn {
        width: 80px;
        height: 30px;
        font-size: 11px;
    }

    .reservation-segmented :deep(.button) {
        font-size: 11px;
        padding: 5px 2px;
    }
}
</style>
